/**
 * The OrionIQ chat effort mode — read and written where every surface can see it.
 *
 * PERSISTENCE MOVED (ORIONIQ-1475). This used to write app-api's app-state KV (`/state/v0/orion-chat-mode`),
 * which the mobile app cannot reach — so the mode a user picked in the browser and the one on their phone
 * were two unrelated values. It is now `chatMode` on `GET|PUT /orioniq-be/user/preferences`, the SAME per-user
 * document mobile already uses, which is what makes the choice follow a person across surfaces rather than
 * across browser tabs. That endpoint was deliberately named `/user/` rather than `/mobile/` for this.
 *
 * WHAT A MODE MEANS is not here either. orioniq-be owns the registry: the directive prose, the labels, and which
 * mode is the default. This module is the read/write half; the display half arrives from the served catalog.
 */
import { type ChatMode } from '@logz-pkg/models';
import { agentsApiService } from './api.service';
import type { IChatModeOptionResponse } from './general-agent.types';

// The catalog shape, re-exported so consumers do not import a wire type to render a menu.
export type IChatModeOption = IChatModeOptionResponse;

/**
 * The LOCAL catalog, standing in whenever orioniq-be's has not arrived: while `GET /orioniq-be/chat-modes` is in
 * flight (as the consumer's placeholder), if it fails, and if it answers empty.
 *
 * Deliberately just the two built-ins. A bundle cannot know what the registry added after it shipped, and
 * inventing entries here would recreate the per-client fork the catalog exists to remove. Keep the labels
 * and icons matching the registry's — a user on a failed fetch should not see different copy for the same
 * mode.
 */
export const FALLBACK_CHAT_MODE_OPTIONS: readonly IChatModeOption[] = [
  { value: 'fast', label: 'Fast', description: 'Quicker, more direct answers', icon: 'bolt-regular', isDefault: true },
  { value: 'advanced', label: 'Advanced', description: 'Deep, thorough investigation', icon: 'sparkles-regular' },
];

/**
 * What a user with no stored preference gets, DERIVED from the fallback catalog rather than restated beside
 * it — two hand-maintained copies of "the default" in one file is a drift waiting to happen, and the drift
 * would show as a picker highlighting one mode while the first send used another.
 *
 * orioniq-be's registry is still the real authority; this only has to hold until the network answers, and must
 * stay byte-equal to the registry's `isDefault` entry.
 */
export const DEFAULT_CHAT_MODE: ChatMode = FALLBACK_CHAT_MODE_OPTIONS.find(option => option.isDefault)?.value ?? 'fast';

/**
 * The user's stored mode.
 *
 * Deliberately NOT validated against a hardcoded list. orioniq-be validates on write and resolves an unknown
 * stored value to its own default on read, so a client-side allowlist could only do one thing: reject a mode
 * the server considers perfectly valid and silently downgrade the user. Whether this client can RENDER the
 * value is the picker's business, decided against the served catalog.
 *
 * Deliberately does NOT catch either, for the same reason `listOptions` does not. The caller latches "already
 * loaded" BEFORE calling and resets it in a catch, precisely so a failed read retries on the next mount —
 * swallowing here makes that catch unreachable, the latch sticks, and a user who picked Advanced sees Fast
 * for the rest of the session with their real preference never read. A rejection is the signal that machinery
 * is built on; the caller decides what to show meanwhile.
 *
 * `||`, not `??`: an empty `chatMode` is as unusable as a missing one, and passing it through would send it.
 */
const get = async (): Promise<ChatMode> => (await agentsApiService.getUserPreferences()).chatMode || DEFAULT_CHAT_MODE;

// Sends ONLY chatMode. The endpoint merges, so the notification toggles and starred agents this app knows
// nothing about are untouched — which is what makes one shared document safe for three clients to write.
const set = async (mode: ChatMode): Promise<void> => {
  await agentsApiService.updateUserPreferences({ chatMode: mode });
};

/**
 * The served catalog, verbatim. Deliberately does NOT catch: a failure must stay a failure.
 *
 * Swallowing it and resolving with `FALLBACK_CHAT_MODE_OPTIONS` would look defensive and behave far worse —
 * the consumer caches this for the session (`staleTime: Infinity`), so a resolved fallback is indistinguish-
 * able from a real catalog and gets pinned for the life of the tab. One transient blip at mount would hide
 * every registry-added mode until reload, with nothing to retry because nothing failed. Rejecting keeps the
 * query retryable, and the consumer owns the render-time fallback.
 *
 * Not cached here either — the consumer holds it in React Query (it changes only on an orioniq-be deploy).
 */
const listOptions = async (): Promise<readonly IChatModeOption[]> => (await agentsApiService.listChatModes()).modes;

export const chatModeService = { get, set, listOptions };

/**
 * Guarantees the selected mode is representable in the returned list.
 *
 * The picker labels its pill with `options.find(o => o.value === value) ?? options[0]`, so a selected mode
 * missing from the options does not render as "unknown" — it renders as the FIRST option. The pill would read
 * "Fast" while every send carried the real mode: the UI actively lying rather than merely being incomplete.
 * Reachable whenever this bundle holds a mode the catalog does not describe — while the fetch is in flight,
 * if it fails, or when a conversation is seeded from a run whose mode came from a newer registry.
 *
 * The synthetic entry carries the raw value as its label because that is the only honest thing available.
 * Its `icon` is empty and the composer maps that to no icon at all — inventing a glyph would claim the mode
 * is one of the ones we recognise, which is the lie this function exists to prevent.
 */
export const withSelected = (
  options: readonly IChatModeOption[],
  selected: ChatMode | undefined,
): readonly IChatModeOption[] =>
  !selected || options.some(option => option.value === selected)
    ? options
    : [...options, { value: selected, label: selected, description: '', icon: '' }];
